import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  CheckSquare,
  Award,
  Grid3X3,
  DollarSign,
} from 'lucide-react';
import {
  useClientEvents,
  useClientExpenseSummary,
  useClientExpenseItems,
  useUpdateClientApproval,
} from '../hooks/usePortal';
import type { ClientEvent, ClientExpenseItem } from '../api/portal';
import { ClientExpenseTable } from '../components/portal/ClientExpenseTable';
import { ClientExpensePaymentModal } from '../components/portal/ClientExpensePaymentModal';
import { MyTasksCard } from '../components/portal/MyTasksCard';

function EventCard({
  event,
  onSelect,
  isSelected,
}: {
  event: ClientEvent;
  onSelect: (event: ClientEvent) => void;
  isSelected: boolean;
}) {
  const navigate = useNavigate();
  const dateStr = new Date(event.startAt).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <button
      onClick={() => {
        onSelect(event);
        navigate(`/client/events/${event.id}`);
      }}
      data-testid={`event-card-${event.id}`}
      className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <h3 className="font-semibold text-lg">{event.name}</h3>
      <div className="mt-2 space-y-1 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar size={14} />
          <span>{dateStr}</span>
        </div>
        {event.eventTypeName && (
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              {event.eventTypeName}
            </span>
          </div>
        )}
        {event.venueName && (
          <div className="flex items-center gap-2">
            <MapPin size={14} />
            <span>{event.venueName}</span>
          </div>
        )}
        {event.guestCount != null && (
          <div className="flex items-center gap-2">
            <Users size={14} />
            <span>{event.guestCount} guests</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              event.status === 'CONFIRMED'
                ? 'bg-green-100 text-green-700'
                : event.status === 'IN_PROGRESS'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-gray-100 text-gray-700'
            }`}
          >
            {event.status}
          </span>
        </div>
      </div>
    </button>
  );
}

function ExpenseSummaryCard({ eventId }: { eventId: string }) {
  const { data: summary, isLoading } = useClientExpenseSummary(eventId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading expense summary...</p>;
  if (!summary) return null;

  return (
    <div data-testid="expense-summary" className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <DollarSign size={18} />
        Budget Summary
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-gray-500">Total Cost</p>
          <p className="text-lg font-bold" data-testid="total-cost">
            ${summary.totalCost.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Total Paid</p>
          <p className="text-lg font-bold text-green-600" data-testid="total-paid">
            ${summary.totalPaid.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Balance</p>
          <p className="text-lg font-bold text-red-600" data-testid="balance">
            ${summary.balance.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Items</p>
          <p className="text-lg font-bold">{summary.itemCount}</p>
        </div>
      </div>
    </div>
  );
}

function ClientExpenseItemsPanel({ eventId, eventName }: { eventId: string; eventName: string }) {
  const { data: items, isLoading } = useClientExpenseItems(eventId);
  const approvalMutation = useUpdateClientApproval();
  const [payFor, setPayFor] = useState<ClientExpenseItem | null>(null);

  if (isLoading) return <p className="text-sm text-gray-500">Loading expense items...</p>;

  return (
    <div data-testid="client-expense-items" className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <DollarSign size={18} />
        Vendor Selections
      </h3>
      <ClientExpenseTable
        items={items ?? []}
        emptyMessage="No expense items visible for this event."
        approvalPending={approvalMutation.isPending}
        onApprovalChange={(item, clientApproval) =>
          approvalMutation.mutate({ expenseItemId: item.id, clientApproval })
        }
        onPay={setPayFor}
      />
      <ClientExpensePaymentModal
        eventId={eventId}
        eventName={eventName}
        item={payFor}
        onClose={() => setPayFor(null)}
      />
    </div>
  );
}

const quickLinks = [
  { label: 'Guest List', icon: Users, path: 'guests' },
  { label: 'Timeline', icon: Clock, path: 'timeline' },
  { label: 'Checklist', icon: CheckSquare, path: 'checklist' },
  { label: 'Kibuddim', icon: Award, path: 'kibuddim' },
  { label: 'Floor Plan', icon: Grid3X3, path: 'floor-plan' },
];

export function ClientDashboardPage() {
  const { data: events, isLoading } = useClientEvents();
  const [selectedEvent, setSelectedEvent] = useState<ClientEvent | null>(null);

  if (isLoading) {
    return <p className="p-4 text-gray-500">Loading your events...</p>;
  }

  if (!events || events.length === 0) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">My Events</h1>
        <p className="text-gray-500">You are not assigned to any events yet.</p>
      </div>
    );
  }

  const budgetVisibility = selectedEvent?.budgetVisibility || 'full';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">My Events</h1>

      <MyTasksCard scope="client" eventPath={(id) => `/client/events/${id}?tab=tasks`} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            onSelect={setSelectedEvent}
            isSelected={selectedEvent?.id === event.id}
          />
        ))}
      </div>

      {selectedEvent && (
        <div className="space-y-4" data-testid="event-detail">
          <h2 className="text-xl font-semibold">{selectedEvent.name}</h2>

          {/* Quick Links */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {quickLinks.map((link) => (
              <a
                key={link.path}
                href={`/events/${selectedEvent.id}/${link.path}`}
                className="flex items-center gap-2 p-3 rounded-lg border bg-white hover:bg-gray-50 text-sm font-medium"
                data-testid={`quick-link-${link.path}`}
              >
                <link.icon size={16} className="text-primary-600" />
                {link.label}
              </a>
            ))}
          </div>

          {/* Expense Summary — only for 'full' or 'summary' visibility */}
          {budgetVisibility !== 'hidden' && <ExpenseSummaryCard eventId={selectedEvent.id} />}

          {/* Expense Items for approval — only for 'full' visibility */}
          {budgetVisibility === 'full' && (
            <ClientExpenseItemsPanel eventId={selectedEvent.id} eventName={selectedEvent.name} />
          )}
        </div>
      )}
    </div>
  );
}
